import { createRouter, createWebHashHistory } from "vue-router";
import { useAuthStore } from "./stores/auth";
import { useChannelStore } from "./stores/channels";

const STARTUP_CHANNEL_PARAM = "__startup__";
const defaultChannelRoute = (name: string) => ({ name, params: { channelId: STARTUP_CHANNEL_PARAM } });

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: "/auth",
      name: "auth",
      component: () => import("./pages/AuthPage.vue"),
      meta: { public: true, title: "登录" }
    },
    {
      path: "/admin",
      component: () => import("./pages/admin/AdminLayout.vue"),
      children: [
        { path: "", redirect: { name: "admin-overview" } },
        { path: "forbidden", name: "admin-forbidden", component: () => import("./pages/admin/AdminForbiddenPage.vue"), meta: { title: "无权限" } },
        { path: "overview", name: "admin-overview", component: () => import("./pages/admin/AdminOverviewPage.vue"), meta: { title: "后台总览", admin: true } },
        { path: "users", name: "admin-users", component: () => import("./pages/admin/AdminUsersPage.vue"), meta: { title: "用户", admin: true } },
        { path: "users/:userId", name: "admin-user-detail", component: () => import("./pages/admin/AdminUserDetailPage.vue"), meta: { title: "用户详情", admin: true } },
        { path: "channels", name: "admin-channels", component: () => import("./pages/admin/AdminChannelsPage.vue"), meta: { title: "频道", admin: true } },
        { path: "channels/:channelId", name: "admin-channel-detail", component: () => import("./pages/admin/AdminChannelDetailPage.vue"), meta: { title: "频道详情", admin: true } },
        { path: "audit", name: "admin-audit", component: () => import("./pages/admin/AdminAuditPage.vue"), meta: { title: "审计", admin: true } },
        { path: "system", name: "admin-system", component: () => import("./pages/admin/AdminSystemPage.vue"), meta: { title: "系统", admin: true } }
      ]
    },
    {
      path: "/",
      component: () => import("./components/AppLayout.vue"),
      children: [
        { path: "", redirect: () => defaultChannelRoute("capture") },
        { path: "capture", redirect: () => defaultChannelRoute("capture") },
        { path: "today", redirect: () => defaultChannelRoute("today") },
        { path: "schedule", redirect: () => defaultChannelRoute("schedule") },
        { path: "review", redirect: () => defaultChannelRoute("review") },
        { path: "memory", redirect: () => defaultChannelRoute("memory") },
        { path: "settings", redirect: () => defaultChannelRoute("settings") },
        { path: "channels/:channelId/capture", name: "capture", component: () => import("./pages/CapturePage.vue"), meta: { title: "记录" } },
        { path: "channels/:channelId/today", name: "today", component: () => import("./pages/TodayPage.vue"), meta: { title: "今日" } },
        { path: "channels/:channelId/schedule", name: "schedule", redirect: (to) => ({ name: "today", params: to.params, query: { ...to.query, tab: "schedule" } }) },
        { path: "channels/:channelId/review", name: "review", redirect: (to) => ({ name: "memory", params: to.params }) },
        { path: "channels/:channelId/memory", name: "memory", component: () => import("./pages/MemoryPage.vue"), meta: { title: "全部记录" } },
        { path: "channels/:channelId/settings", name: "settings", component: () => import("./pages/SettingsPage.vue"), meta: { title: "设置" } }
      ]
    },
    { path: "/:pathMatch(.*)*", redirect: () => defaultChannelRoute("capture") }
  ]
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const channels = useChannelStore();
  await auth.boot();
  if (!auth.user && !to.meta.public) return { name: "auth" };
  if (auth.user && to.name === "auth") return defaultChannelRoute("capture");
  if (auth.user && to.matched.some((record) => record.meta.admin) && (!auth.user.admin_role || auth.user.admin_role === "none")) {
    return { name: "admin-forbidden" };
  }
  if (auth.user && typeof to.params.channelId === "string") {
    if (channels.channels.length === 0) await channels.loadChannels().catch(() => undefined);
    const resolvedChannelId = to.params.channelId === STARTUP_CHANNEL_PARAM
      ? channels.resolveStartupChannelId()
      : channels.resolveChannelId(to.params.channelId);
    if (resolvedChannelId !== to.params.channelId) {
      return { name: to.name || "capture", params: { ...to.params, channelId: resolvedChannelId }, query: to.query, hash: to.hash };
    }
    await channels.switchChannel(resolvedChannelId);
  }
  return true;
});

export default router;
